import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SearchResult } from '@shared/types';

function titleFor(pathname: string): string {
  if (pathname === '/' || pathname.startsWith('/collection')) return 'My Collection';
  if (pathname.startsWith('/add')) return 'Add a card';
  if (pathname.startsWith('/result')) return 'Nice catch!';
  if (pathname.startsWith('/me')) return 'You';
  if (pathname.startsWith('/item/')) return 'Card';
  return '';
}

type SearchMode = 'collection' | 'catalog' | null;

function modeFor(pathname: string): SearchMode {
  if (pathname === '/' || pathname.startsWith('/collection')) return 'collection';
  if (pathname.startsWith('/add')) return 'catalog';
  return null;
}

export default function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const title = titleFor(location.pathname);
  const searchMode = modeFor(location.pathname);
  const canBack =
    location.pathname === '/result' || location.pathname.startsWith('/item/');

  const isCollection = searchMode === 'collection';

  // --- Local search state (used by both modes; Collection also mirrors to URL `q`) ---
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Tracks whether the route-change/hydration effect has run for the current
  // pathname. Mirror-to-URL must wait for hydration so it doesn't clobber `q`
  // on first render. Re-arms on every pathname change.
  const hydratedFor = useRef<string | null>(null);

  // Collection: mirror input → URL `q` so Collection.tsx can filter.
  // Skipped until hydration has run for this pathname.
  useEffect(() => {
    if (searchMode !== 'collection') return;
    if (hydratedFor.current !== location.pathname) return;
    const next = new URLSearchParams(params);
    if (searchInput.trim()) next.set('q', searchInput);
    else next.delete('q');
    setParams(next, { replace: true });
    // Only depend on the input value; setParams + params change identity often.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, searchMode]);

  // Catalog: debounce for API
  useEffect(() => {
    if (searchMode !== 'catalog') return;
    const t = setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, searchMode]);

  // Focus the input the moment it expands (iOS PWA-safe)
  useEffect(() => {
    if (searchOpen) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
  }, [searchOpen]);

  // Close search + reset state on route change. Also hydrates from URL `q`
  // on Collection so a refresh with ?q=foo restores the open search bar.
  useEffect(() => {
    setDebouncedQuery('');
    if (searchMode === 'collection') {
      const q = params.get('q') ?? '';
      if (q) {
        setSearchInput(q);
        setSearchOpen(true);
      } else {
        setSearchInput('');
        setSearchOpen(false);
      }
    } else {
      setSearchInput('');
      setSearchOpen(false);
    }
    // Mark this pathname hydrated so the mirror-to-URL effect can run.
    hydratedFor.current = location.pathname;
    // Intentionally only depend on pathname so this runs on route changes,
    // not whenever `params` identity churns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Outside-click dismiss for catalog dropdown only.
  // Collection search is URL-persistent — clicking outside shouldn't clear it.
  useEffect(() => {
    if (!searchOpen || searchMode !== 'catalog') return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchInput('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [searchOpen, searchMode]);

  // --- Filter button state (collection only) ---
  const filtersOpen = params.get('filters') === '1';
  const hasActiveFilter = !!(params.get('g') || params.get('t'));

  function toggleFilters() {
    const next = new URLSearchParams(params);
    if (next.get('filters') === '1') next.delete('filters');
    else next.set('filters', '1');
    setParams(next, { replace: true });
  }

  // --- Catalog search results ---
  const catalogQuery = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: () => api.search(debouncedQuery),
    enabled: searchMode === 'catalog' && searchOpen && debouncedQuery.length >= 2,
  });

  function pickCatalogResult(r: SearchResult) {
    sessionStorage.setItem(
      'cvpc:searchPick',
      JSON.stringify({
        game: r.game,
        type: 'single',
        name: r.name,
        set_name: r.set_name,
        card_number: r.card_number,
        confidence: 1,
        image_url: r.image_url,
      }),
    );
    // Closing happens automatically via the route-change effect, but clear here
    // too so the dropdown vanishes the instant we navigate (no flash).
    setSearchOpen(false);
    setSearchInput('');
    navigate('/result?source=search');
  }

  function closeSearch() {
    setSearchOpen(false);
    setSearchInput('');
    if (searchMode === 'collection') {
      const next = new URLSearchParams(params);
      next.delete('q');
      setParams(next, { replace: true });
    }
  }

  const placeholderText =
    searchMode === 'catalog' ? 'Search Pokémon, Lorcana…' : 'Search your cards';

  return (
    <header
      ref={containerRef}
      className="sticky top-0 z-30 backdrop-blur-lg"
      style={{
        background: 'rgba(10, 13, 18, 0.7)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="max-w-md mx-auto px-4 h-14 flex items-center gap-2">
        {/* Back arrow */}
        {canBack && !searchOpen && (
          <button
            onClick={() => navigate(-1)}
            className="-ml-2 p-2 rounded-lg shrink-0"
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* Title — fades out when search opens; search input takes the same flex slot */}
        {!searchOpen && (
          <h1 className="flex-1 font-display text-[20px] font-semibold truncate min-w-0">
            {title}
          </h1>
        )}

        {/* Inline search input — takes the flex slot when open */}
        {searchOpen && searchMode && (
          <div className="flex-1 min-w-0">
            <div
              className="flex items-center gap-2 px-3 h-9 rounded-xl"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--accent)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-text-dim shrink-0">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                ref={inputRef}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={placeholderText}
                inputMode="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-text-faint"
              />
            </div>
          </div>
        )}

        {/* Right-side action buttons (search closed, search affordance available) */}
        {searchMode && !searchOpen && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
              style={{
                background: 'var(--bg-elev)',
                border: '1px solid var(--border)',
                color: 'var(--text-soft)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16" y2="16" strokeLinecap="round" />
              </svg>
            </button>

            {isCollection && (
              <button
                onClick={toggleFilters}
                aria-label="Filter collection"
                className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
                style={{
                  background: filtersOpen ? 'var(--accent-soft)' : 'var(--bg-elev)',
                  border: `1px solid ${filtersOpen ? 'var(--accent)' : 'var(--border)'}`,
                  color: filtersOpen ? 'var(--accent)' : 'var(--text-soft)',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                {hasActiveFilter && (
                  <span
                    className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                    style={{ background: 'var(--accent)' }}
                  />
                )}
              </button>
            )}
          </div>
        )}

        {/* Close (X) — replaces action buttons while search is open */}
        {searchOpen && (
          <button
            onClick={closeSearch}
            aria-label="Close search"
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              color: 'var(--text-soft)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* Catalog results dropdown — only on Add, below TopBar */}
      {searchMode === 'catalog' && searchOpen && debouncedQuery.length >= 2 && (
        <div
          className="absolute left-0 right-0 top-full"
          style={{ background: 'transparent' }}
        >
          <div className="max-w-md mx-auto px-4">
            <div
              className="mt-1 max-h-[60vh] overflow-y-auto rounded-2xl"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-bright)',
                boxShadow: '0 12px 32px -8px rgba(0,0,0,0.55)',
              }}
            >
              {catalogQuery.isLoading && (
                <div className="p-3 text-sm text-text-dim flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full border-2 animate-spin"
                    style={{
                      borderColor: 'var(--accent)',
                      borderTopColor: 'transparent',
                    }}
                  />
                  Searching…
                </div>
              )}
              {!catalogQuery.isLoading && catalogQuery.data?.results.length === 0 && (
                <div className="p-3 text-sm text-text-dim">No matches.</div>
              )}
              {catalogQuery.data?.results.map((r, i) => (
                <button
                  key={`${r.game}-${r.card_id}`}
                  onClick={() => pickCatalogResult(r)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:opacity-80 transition-opacity"
                  style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}
                >
                  <div
                    className="w-9 h-12 rounded shrink-0 overflow-hidden"
                    style={{ background: 'var(--bg-elev)' }}
                  >
                    {r.image_url && (
                      <img src={r.image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-xs text-text-dim truncate">
                      {r.set_name} · {r.game === 'pokemon' ? 'Pokémon' : 'Lorcana'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
